// doc-card-chips.ts — deterministic follow-up chips for a doc-card.
//
// The card must offer 3-4 topical follow-ups the moment it opens, and opening
// help is a ZERO-LLM operation (docs-import-plan.md, arbitration #2). So the
// chips are derived from what the host already handed us — the article title —
// by a pure function, never by asking a model.
//
// Host-supplied chips always win: the portal knows its sections and can seed
// better ones (they arrive through <emma-chat>.showDocCard({chips})). This is
// the fallback for hosts that pass none.
//
// The generated `prompt` is a full question, not a bare topic label: the BFF's
// grounding lane gets a real query to retrieve against, and the echoed user
// bubble reads like something a person would type. (The chip still carries
// origin='help', which is what actually routes it to grounding — see
// emma-chat.ts handleChipClick.)

import type { DocCardChip } from '../state/chat-state-store.js';

const TITLE_MAX = 60;

/** Trim an article title down to something that reads inside a sentence:
 *  "Quickstart — emma AI Console" → "Quickstart". */
function shortTitle(title: string): string {
  const head = title.split(/\s+[—–|]\s+/)[0] ?? title;
  return head.trim().slice(0, TITLE_MAX) || title.trim().slice(0, TITLE_MAX);
}

/**
 * Default follow-ups for an article. Deterministic (same title → same chips),
 * capped at 3, no model call. Returns [] for an empty title so the card simply
 * renders without a chip row rather than with nonsense.
 */
export function defaultDocCardChips(title: string): DocCardChip[] {
  const t = shortTitle((title ?? '').toString());
  if (!t) return [];
  return [
    {
      label: 'Summarize this',
      prompt: `Summarize the "${t}" documentation page in a few sentences.`,
    },
    { label: 'Step by step', prompt: `Walk me through ${t} step by step.` },
    { label: 'Common problems', prompt: `What usually goes wrong with ${t}, and how do I fix it?` },
  ];
}

/** Chips to render under a card: host-seeded when present, else derived. */
export function docCardChips(hostChips: DocCardChip[] | undefined, title: string): DocCardChip[] {
  if (hostChips && hostChips.length > 0) return hostChips;
  return defaultDocCardChips(title);
}
